/**
 * The ground move's reticle while it is aimed (design docs/design/ui/2026-10-11-ground-aim/README.md, option A; replaces
 * the smooth ellipses that were in cursor.ts): the reach round the player as a dashed 1-px ring in the art's light tone over a dark edge,
 * the blast circle at the mouse (or the touch / pad point, groundAim.ts) as a 50 % checker in the art's tint with a 1-px
 * dark outline, a light ring and a 3×3 pip. Past the reach it is held to the edge and its ring goes dim (every other dot).
 */
import { groundAim } from '../../groundAim';
import { checkerFill, dotRing, pip, rampOfSkill, stepped, tone, type GroundPen } from '../groundMarkKit';
import { registerRenderPass } from './registry';

registerRenderPass({
  id: 'aim',
  layer: 'ground',
  order: 240,
  draw({ g, world: w, iso, ox, oy, renderer: r }) {
    const a = r.aim;
    if (!a) return;
    const p = w.player;
    let cx: number;
    let cy: number;
    let out = false;
    if (groundAim.at) [cx, cy] = [groundAim.at.x, groundAim.at.y];
    else {
      if (!r.hoverClient) return;
      const [hx, hy] = r.screenToWorld(...r.hoverClient);
      if (!Number.isFinite(hx) || !Number.isFinite(hy)) return;
      let [dx, dy] = [hx - p.x, hy - p.y];
      const d = Math.hypot(dx, dy);
      if (d > a.range) {
        out = true;
        [dx, dy] = [(dx / d) * a.range, (dy / d) * a.range];
      }
      [cx, cy] = [p.x + dx, p.y + dy];
    }
    const skill = w.activeSkills()[a.slot];
    const ramp = skill ? rampOfSkill(skill.id) : 'azure';
    const pen: GroundPen = { g, iso, ox, oy };
    g.save();
    g.globalAlpha = 1;
    // The reach: a dashed ring (light over a dark 1-px edge, readable on pale and dark ground) stepping round at 6 a second.
    const ph = Math.floor(stepped(w.time) * 6) % 4;
    dotRing(pen, p.x, p.y, a.range + 0.05, tone(ramp, 0), 4, ph, 2);
    dotRing(pen, p.x, p.y, a.range, tone(ramp, 5), 4, ph, 2);
    // The blast.
    checkerFill(pen, cx, cy, a.radius, tone(ramp, out ? 2 : 3));
    dotRing(pen, cx, cy, a.radius + 0.06, tone(ramp, 0));
    dotRing(pen, cx, cy, a.radius, tone(ramp, out ? 3 : 6), out ? 2 : 1);
    pip(pen, cx, cy, tone(ramp, 6), tone(ramp, 0));
    g.restore();
  },
});
